import VoxelStatistics, { registerVoxelStatisticsConstant, } from '../../enums/VoxelStatistics.js';
import VoxelReductions from '../../enums/VoxelReductions.js';
import { reduceAverageAlongKBySlices, reduceForegroundMajorityBySlices, } from './sliceReductions.js';
import fillPlaneByAverage from './averagePlane.js';
import fillPlaneByForegroundMajority from './foregroundMajorityPlane.js';
const statisticDefinitions = new Map();
const registeredConstantNames = [];
let hasRegisteredCoreVoxelStatistics = false;
function createAverageAccumulator() {
    let sum = 0;
    let count = 0;
    return {
        reset: () => {
            sum = 0;
            count = 0;
        },
        add: (value, weight = 1) => {
            sum += value * weight;
            count += weight;
        },
        getValue: () => (count === 0 ? undefined : sum / count),
    };
}
function createForegroundMajorityAccumulator() {
    const counts = new Map();
    let majority;
    let majorityCount = 0;
    let sawBackground = false;
    return {
        reset: () => {
            counts.clear();
            majority = undefined;
            majorityCount = 0;
            sawBackground = false;
        },
        add: (value, weight = 1) => {
            if (value === 0) {
                sawBackground = true;
                return;
            }
            const next = (counts.get(value) ?? 0) + weight;
            counts.set(value, next);
            if (next > majorityCount) {
                majorityCount = next;
                majority = value;
            }
        },
        getValue: () => {
            if (majority !== undefined) {
                return majority;
            }
            return sawBackground ? 0 : undefined;
        },
    };
}
function registerCoreVoxelStatistics() {
    if (hasRegisteredCoreVoxelStatistics) {
        return;
    }
    hasRegisteredCoreVoxelStatistics = true;
    registerVoxelStatistic({
        statistic: VoxelStatistics.Average,
        defaultSelection: true,
        description: 'The mean of the source voxels of one box.',
        createAccumulator: createAverageAccumulator,
        reduceBySlices: reduceAverageAlongKBySlices,
        fillPlane: fillPlaneByAverage,
        boxReduction: VoxelReductions.BoxAverage,
    });
    registerVoxelStatistic({
        statistic: VoxelStatistics.ForegroundMajority,
        defaultSelection: false,
        description: 'The majority non-zero label of one box (background ignored unless the box is empty). Used to reduce labelmaps.',
        createAccumulator: createForegroundMajorityAccumulator,
        reduceBySlices: reduceForegroundMajorityBySlices,
        fillPlane: fillPlaneByForegroundMajority,
        boxReduction: VoxelReductions.BoxForegroundMajority,
    });
}
function registerVoxelStatistic({ statistic, name, defaultSelection = false, description, createAccumulator, reduceBySlices, fillPlane, boxReduction, }) {
    if (!statistic) {
        throw new Error('registerVoxelStatistic: the definition needs a statistic');
    }
    if (statisticDefinitions.has(statistic)) {
        throw new Error(`Voxel statistic "${statistic}" is already registered`);
    }
    if (typeof createAccumulator !== 'function') {
        throw new Error(`Voxel statistic "${statistic}" must declare a createAccumulator function`);
    }
    for (const [field, hook] of Object.entries({ reduceBySlices, fillPlane })) {
        if (hook !== undefined && typeof hook !== 'function') {
            throw new Error(`Voxel statistic "${statistic}" declares ${field}, which is not a function`);
        }
    }
    if (name && Object.prototype.hasOwnProperty.call(VoxelStatistics, name)) {
        throw new Error(`Voxel statistic constant "${String(name)}" already exists`);
    }
    statisticDefinitions.set(statistic, {
        statistic,
        defaultSelection,
        description,
        createAccumulator,
        reduceBySlices,
        fillPlane,
        boxReduction,
    });
    if (name) {
        registerVoxelStatisticsConstant(name, statistic);
        registeredConstantNames.push(name);
    }
}
function getVoxelStatistic(statistic) {
    registerCoreVoxelStatistics();
    return statisticDefinitions.get(statistic);
}
function requireVoxelStatistic(statistic) {
    const definition = getVoxelStatistic(statistic);
    if (!definition) {
        throw new Error(`Voxel statistic "${statistic}" is not registered. Call registerVoxelStatistic first.`);
    }
    return definition;
}
function getVoxelStatistics() {
    registerCoreVoxelStatistics();
    return [...statisticDefinitions.values()];
}
function isRegisteredVoxelStatistic(statistic) {
    return getVoxelStatistic(statistic) !== undefined;
}
function isDefaultSelectionStatistic(statistic) {
    return getVoxelStatistic(statistic)?.defaultSelection === true;
}
function __resetVoxelStatisticRegistry() {
    statisticDefinitions.clear();
    hasRegisteredCoreVoxelStatistics = false;
    for (const name of registeredConstantNames) {
        delete VoxelStatistics[name];
    }
    registeredConstantNames.length = 0;
}
export { registerVoxelStatistic, getVoxelStatistic, requireVoxelStatistic, getVoxelStatistics, isRegisteredVoxelStatistic, isDefaultSelectionStatistic, createAverageAccumulator, __resetVoxelStatisticRegistry, };
